/**
 * Toast.jsx - Toast Notification Component
 * 
 * @description
 * Provides toast notifications for user feedback throughout the application.
 * Includes a custom hook (useToast) for easy integration in any component.
 * 
 * @exports
 * - ToastProvider: Context provider for toast functionality
 * - useToast: Hook to show toast notifications
 * - ToastContainer: Component to render active toasts
 * 
 * @usage
 * ```jsx
 * // In component:
 * const { showSuccess, showError, ToastContainer } = useToast();
 * 
 * // Show notifications:
 * showSuccess('Profile updated successfully!');
 * showError('Failed to save changes');
 * 
 * // Render container:
 * return (
 *   <>
 *     <ToastContainer />
 *     {/* Your component content *\/}
 *   </>
 * );
 * ```
 * 
 * @features
 * - Success and error toast types
 * - Auto-dismiss after 3 seconds
 * - Manual dismiss option
 * - Smooth animations
 * - Stacked toast display
 * - Responsive positioning
 * 
 * @toastStructure
 * {
 *   id: number - Unique identifier
 *   message: string - Toast message
 *   type: 'success' | 'error' - Toast variant
 * }
 * 
 * @styling
 * - Success: Green with checkmark icon
 * - Error: Red with X icon
 * - Fixed positioning at top-right
 * - Responsive margins
 * 
 * @animations
 * - Slide in from right
 * - Fade out on dismiss
 * - Smooth height transitions
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { createContext, useContext } from 'react';

// Create context
const ToastContext = createContext();

const Toast = ({ message, type = 'info', duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for animation to finish
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const typeStyles = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
  };

  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };

  return createPortal(
    <div
      className={`fixed bottom-4 right-4 transform transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      } z-50`}
    >
      <div
        className={`${typeStyles[type]} text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px]`}
      >
        <span className="text-xl">{icons[type]}</span>
        <p className="flex-1">{message}</p>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="text-white/80 hover:text-white"
        >
          ✕
        </button>
      </div>
    </div>,
    document.body
  );
};

// Toast Manager Hook
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random(); // Add randomness to prevent duplicate IDs
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message) => showToast(message, 'success'), [showToast]);
  const showError = useCallback((message) => showToast(message, 'error'), [showToast]);
  const showWarning = useCallback((message) => showToast(message, 'warning'), [showToast]);
  const showInfo = useCallback((message) => showToast(message, 'info'), [showToast]);

  return {
    toasts,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    ToastContainer: () => (
      <>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </>
    ),
  };
};

// Toast provider component
export const ToastProvider = ({ children }) => {
  // ... existing code ...
};

export default Toast; 